import { resolveLocale } from './formatDate';

/**
 * Progesteronwerte werden immer in ng/ml gespeichert und uebertragen.
 *
 * Je nach Labor wird aber in ng/ml oder nmol/l gemessen. Deshalb haengt an jedem
 * Eintrag die Einheit, in der er erfasst wurde - sie steuert Eingabe und Anzeige,
 * nicht den gespeicherten Wert.
 */

export type ProgesteroneUnit = 'ng/ml' | 'nmol/l';

/** Umrechnungsfaktor Progesteron: 1 ng/ml = 3.18 nmol/l (Molmasse 314.46 g/mol). */
const NMOLL_PER_NGML = 3.18;

/** Alles ausser "nmol/l" gilt als ng/ml - auch Altbestand ohne Einheit. */
export const normalizeUnit = (unit: string | null | undefined): ProgesteroneUnit =>
  unit?.trim().toLowerCase() === 'nmol/l' ? 'nmol/l' : 'ng/ml';

/** Eingabewert -> ng/ml (Speicherformat). */
export const toNgMl = (value: number, unit: ProgesteroneUnit): number =>
  unit === 'nmol/l' ? value / NMOLL_PER_NGML : value;

/** ng/ml -> Anzeigewert in der gewuenschten Einheit. */
export const fromNgMl = (ngMl: number, unit: ProgesteroneUnit): number =>
  unit === 'nmol/l' ? ngMl * NMOLL_PER_NGML : ngMl;

/**
 * Formatiert einen gespeicherten Progesteronwert in der Einheit, in der er erfasst wurde.
 */
export const formatProgesterone = (ngMl: number, unit?: string | null): string => {
  const target = normalizeUnit(unit);
  const value = fromNgMl(ngMl, target);
  const formatted = value.toLocaleString(resolveLocale(), {
    minimumFractionDigits: 0,
    maximumFractionDigits: target === 'nmol/l' ? 2 : 1
  });
  return `${formatted} ${target}`;
};

/**
 * Formatiert einen gespeicherten Progesteronwert in beiden Einheiten -
 * zuerst die Erfassungseinheit, dann die jeweils andere in Klammern.
 */
export const formatProgesteroneBoth = (ngMl: number, unit?: string | null): string => {
  const primary = normalizeUnit(unit);
  const secondary: ProgesteroneUnit = primary === 'nmol/l' ? 'ng/ml' : 'nmol/l';
  return `${formatProgesterone(ngMl, primary)} (${formatProgesterone(ngMl, secondary)})`;
};
